import { Autocomplete, Box, Button, CircularProgress, Grid, TextField, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import React, { memo, useEffect, useState } from 'react';
import Textfield from '../../components/input/Textfield';
import * as Yup from 'yup';
import { BASE_URL, apiRequest } from '../../utils/api';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchCustomerData } from '../../redux/slices/customerSlice';
import { fetchRepsData } from '../../redux/slices/repSlice';
import { fetchBankData } from '../../redux/slices/bankSlice';
import { fetchVendorData } from '../../redux/slices/vendorSlice';
import axios from 'axios';
import moment from 'moment/moment';

const AddNew = ({ onClose }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    let { user } = useSelector((state) => state?.user);

    const { data: reps, isLoading: isLoadingReps, error: errorReps } = useSelector((state) => state.rep);
    const { data: customer, isLoading: isLoadingCustomer, error: errorCustomer } = useSelector((state) => state.customer);
    const { data: bank, isLoading: isLoadingBank, error: errorBank } = useSelector((state) => state.bank);
    const { data: vendor, isLoading: isLoadingVendor, error: errorVendor } = useSelector((state) => state.vendor);

    const [selectedRep, setSelectedRep] = React.useState(null);
    const [selectedCustomer, setSelectedCustomer] = React.useState(null);
    const [selectedBank, setSelectedBank] = React.useState(null);
    const [selectedVendor, setSelectedVendor] = React.useState(null);

    const [createdOn, setCreatedOn] = useState(moment().format('YYYY-MM-DDTHH:mm'));

    useEffect(() => {
        if (!reps) {
            dispatch(fetchRepsData());
        }
        if (!customer) {
            dispatch(fetchCustomerData());
        }
        if (!bank) {
            dispatch(fetchBankData());
        }
        if (!vendor) {
            dispatch(fetchVendorData());
        }

        return () => {
            setSelectedRep(null);
            setSelectedBank(null);
            setSelectedVendor(null);
            setSelectedCustomer(null);
        };
    }, []);

    const INITIAL_FORM_STATE = {
        amount: '',
        comments: '',
        refNo: ''
    };

    const FORM_VALIDATION = Yup.object().shape({
        amount: Yup.number().typeError('Amount must be a number')
    });

    const [uploadedFile, setUploadedFile] = useState(null);

    const handleOnChangeUploadReport = async (file) => {
        let imageName = file?.[0]?.name;

        let image = {
            name: imageName,
            image: file?.[0],
            url: URL.createObjectURL(file?.[0])
        };

        setUploadedFile(image);
    };

    const handleSubmit = async (values) => {
        try {
            if (selectedRep == null) {
                return toast.error('Please select rep');
            }
            if (selectedCustomer == null) {
                return toast.error('Please select customer');
            }
            if (selectedBank == null) {
                return toast.error('Please select bank');
            }
            const token = JSON.parse(localStorage.getItem('token'));

            let formdata = new FormData();
            formdata.append('user', selectedRep);
            formdata.append('customer', selectedCustomer);
            formdata.append('bank', selectedBank);
            formdata.append('amount', values?.amount);
            formdata.append('comments', values?.comments);
            selectedVendor && formdata.append('vendor', selectedVendor);
            formdata.append('refNo', values?.refNo);
            formdata.append('createdOn', moment(createdOn).utc().toISOString());
            uploadedFile && formdata.append('slip', uploadedFile?.image, uploadedFile?.name);

            let res = await axios({
                method: 'post',
                url: `${BASE_URL}payments`,
                data: formdata,
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (res?.data) {
                onClose(true);
            }
        } catch (error) {
            toast.error(error?.response?.data?.message ?? error?.response?.data ?? error?.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            {isLoadingReps || isLoadingCustomer || isLoadingBank ? (
                <CircularProgress size={35} color="inherit" />
            ) : (
                <Formik
                    initialValues={{
                        ...INITIAL_FORM_STATE
                    }}
                    validationSchema={FORM_VALIDATION}
                    onSubmit={(values, { resetForm }) => {
                        setLoading(true);
                        handleSubmit(values, resetForm);
                    }}
                >
                    <Form>
                        <Grid container direction="row" spacing={2}>
                            <Grid item lg={6} sm={6} md={6} xs={12}>
                                <Autocomplete
                                    options={reps ?? []}
                                    getOptionLabel={(rep) => `${rep?.firstName} ${rep?.lastName}`}
                                    value={reps?.find((i) => i?.id == selectedRep)}
                                    onChange={(event, selected) => {
                                        setSelectedRep(selected?.id || null);
                                        setSelectedCustomer(null);
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Rep" variant="standard" />}
                                />
                            </Grid>

                            <Grid item lg={6} sm={6} md={6} xs={12}>
                                <Autocomplete
                                    options={selectedRep ? customer?.filter((i) => i?.user?._id == selectedRep) : []}
                                    getOptionLabel={(c) => `${c?.name}`}
                                    value={customer ? customer?.find((i) => i?.id == selectedCustomer) : null}
                                    onChange={(event, selected) => {
                                        setSelectedCustomer(selected?.id || null);
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Customer" variant="standard" />}
                                />
                            </Grid>

                            <Grid item lg={6} sm={6} md={6} xs={12}>
                                <Autocomplete
                                    options={bank ?? []}
                                    getOptionLabel={(b) => `${b?.name}`}
                                    value={bank?.find((i) => i?.id == selectedBank)}
                                    onChange={(event, selected) => {
                                        setSelectedBank(selected?.id || null);
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Bank" variant="standard" />}
                                />
                            </Grid>

                            <Grid item lg={6} md={4} sm={4} xs={12}>
                                <Textfield name="amount" label="Amount" />
                            </Grid>

                            <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Textfield name="comments" label="Comments" />
                            </Grid>

                            <Grid item lg={6} sm={6} md={6} xs={12}>
                                <Autocomplete
                                    options={vendor ?? []}
                                    getOptionLabel={(b) => `${b?.name}`}
                                    value={vendor?.find((i) => i?._id == selectedVendor)}
                                    onChange={(event, selected) => {
                                        setSelectedVendor(selected?._id || null);
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Vendor" variant="standard" />}
                                />
                            </Grid>

                            <Grid item lg={6} sm={6} md={6} xs={12}>
                                <Textfield name="refNo" label="Ref No." />
                            </Grid>

                            <Grid item lg={6} md={6} sm={6} xs={12}>
                                <TextField
                                    size="small"
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleOnChangeUploadReport(e.target.files)}
                                />
                            </Grid>

                            {(user?.email == 'imran-saleem@kkfoods.com' || user?.role == 'super-admin' || user?.role == 'cash-payments' || user?.role == 'account') && (
                                <Grid item lg={6} md={6} sm={6} xs={12}>
                                    <TextField
                                        label="Date Time"
                                        value={createdOn}
                                        onChange={(e) => setCreatedOn(e.target.value)}
                                        fullWidth
                                        size="small"
                                        type="datetime-local"
                                    />
                                </Grid>
                            )}
                        </Grid>

                        <Box sx={styles.btnContainer}>
                            {loading ? (
                                <CircularProgress size={25} color="inherit" />
                            ) : (
                                <>
                                    {/* -------------- onClick will call the onClose function that closes the History Modal */}
                                    <Button onClick={onClose} variant="text" sx={{ color: 'red' }}>
                                        Cancel
                                    </Button>

                                    {/* -----------  change button title on the basis of history status */}
                                    <Button type="submit" variant="text">
                                        Save
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Form>
                </Formik>
            )}
        </div>
    );
};

export default memo(AddNew);

const styles = {
    loadingContainer: {
        height: 400,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    btnContainer: {
        display: 'flex',
        direction: 'row',
        gap: 1,
        marginTop: 1,
        justifyContent: 'flex-end'
    }
};
